const fuzzyMatchSingle = (query, target, usedPositions) => {
    const indices = [];
    let queryIndex = 0;
    let lastMatchIndex = -1;
    let score = 0;
    let consecutiveCount = 0;
    for (let targetIndex = 0; targetIndex < target.length && queryIndex < query.length; targetIndex++) {
        if (usedPositions.has(targetIndex))
            continue;
        if (target[targetIndex] !== query[queryIndex])
            continue;
        indices.push(targetIndex);
        const gap = lastMatchIndex === -1 ? targetIndex : targetIndex - lastMatchIndex - 1;
        if (gap === 0 && lastMatchIndex !== -1) {
            consecutiveCount++;
        }
        else {
            score += gap * 2;
            consecutiveCount = 0;
        }
        score -= consecutiveCount * 0.5;
        if (targetIndex === 0 || /[\s\-_,.]/.test(target[targetIndex - 1])) {
            score -= 3;
        }
        lastMatchIndex = targetIndex;
        queryIndex++;
    }
    return queryIndex === query.length ? { score, indices } : null;
};
const fuzzyMatchField = (query, target) => {
    if (!query || !target)
        return null;
    const normalizedQuery = query.toLowerCase();
    const normalizedTarget = target.toLowerCase();
    if (normalizedQuery.length > normalizedTarget.length)
        return null;
    const substringIndex = normalizedTarget.indexOf(normalizedQuery);
    if (substringIndex !== -1) {
        const indices = Array.from({ length: normalizedQuery.length }, (_, index) => substringIndex + index);
        const boundaryBonus = substringIndex === 0 || /\s/.test(target[substringIndex - 1]) ? 0 : 5;
        return {
            score: boundaryBonus + substringIndex * 0.5,
            indices,
        };
    }
    const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
    if (queryWords.length > 1) {
        let score = 0;
        const indices = [];
        const usedPositions = new Set();
        for (const word of queryWords) {
            const match = fuzzyMatchSingle(word, normalizedTarget, usedPositions);
            if (!match)
                return null;
            score += match.score;
            for (const index of match.indices) {
                indices.push(index);
                usedPositions.add(index);
            }
        }
        return { score: score + 10, indices: indices.sort((a, b) => a - b) };
    }
    const match = fuzzyMatchSingle(normalizedQuery, normalizedTarget, new Set());
    return match ? { score: match.score + 20, indices: match.indices } : null;
};
/**
 * Fuzzy-searches string fields and returns the best matches first. Exact
 * substrings, consecutive characters, word boundaries, and early matches are
 * favored by the scoring model used by the desk booking UI.
 */
export const fuzzySearch = (items, query, fields) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
        return items.map((item) => ({
            item,
            score: 0,
            matchedIndices: new Map(),
        }));
    }
    const results = [];
    for (const item of items) {
        let bestScore = Infinity;
        const matchedIndices = new Map();
        fields.forEach((field, fieldIndex) => {
            const value = item[field];
            if (typeof value !== 'string')
                return;
            const match = fuzzyMatchField(trimmedQuery, value);
            if (!match)
                return;
            matchedIndices.set(String(field), match.indices);
            bestScore = Math.min(bestScore, match.score + fieldIndex * 10);
        });
        if (matchedIndices.size > 0) {
            results.push({ item, score: bestScore, matchedIndices });
        }
    }
    return results.sort((first, second) => first.score - second.score);
};
